import React from 'react';
import { formatDate, TimeFormats } from './DateFormat.utils';
import { DateFormats } from './DateFormat.enums';
import { useSonTranslation } from '../../../translation/useSonTranslation';

export type DateFormatProps = {
  date?: string;
  format?: DateFormats;
  className?: string;
};
/**
 * Helper component which should be used for localisation
 * of dates (eg. 14.01.2017 or 01.14.2017, etc.)
 */
export function DateFormat({ date, format, className }: DateFormatProps) {
  const { t } = useSonTranslation('DateFormat');
  const clockString = t('oClock');

  if (!date) {
    return null;
  }

  const getDateTimeClosingBrace = () => (
    <>
      {formatDate(date, TimeFormats.HOUR_MINUTE)} {clockString})
    </>
  );

  const getDateTimeHidden = () => (
    <>
      {formatDate(date, TimeFormats.DAY_MONTH_YEAR)}
      <span className="invisible">
        , {formatDate(date, TimeFormats.HOUR_MINUTE)} {clockString}
      </span>
    </>
  );

  const getTime = () => {
    switch (format) {
      case DateFormats.DATE_TIME:
        return `${formatDate(date, TimeFormats.DAY_MONTH_YEAR_HOURS_MINUTES)} ${clockString}`;
      case DateFormats.DATE_TIME_SECONDS:
        return `${formatDate(date, TimeFormats.DAY_MONTH_YEAR_HOURS_MINUTES_SECONDS)} ${clockString}`;
      case DateFormats.DATE_TIME_BRACES:
        return `${formatDate(date, TimeFormats.DAY_MONTH_YEARH_OPEN_BRACET_HOUR_MINUTE)} ${clockString})`;
      case DateFormats.DATE_TIME_OPEN_BRACE:
        return `${formatDate(date, TimeFormats.DAY_MONTH_YEARH_OPEN_BRACET_HOUR_MINUTE)}`;
      case DateFormats.DATE_WITH_SHORT_NAME_OF_THE_WEEK:
        return formatDate(date, TimeFormats.SHORTNAME_WEEK_DAY_MONTH_YEAR);
      case DateFormats.MONTH_AND_YEAR:
        return formatDate(date, TimeFormats.MONTH_YEAR);
      case DateFormats.YEAR:
        return formatDate(date, TimeFormats.YEAR);
      case DateFormats.DATE_TIME_CLOSING_BRACE:
        return getDateTimeClosingBrace();
      case DateFormats.DATE_TIME_HIDDEN:
        return getDateTimeHidden();
      case DateFormats.DATE:
      default:
        return formatDate(date, TimeFormats.DAY_MONTH_YEAR);
    }
  };

  return (
    <time dateTime={date} className={className}>
      {getTime()}
    </time>
  );
}
